import { type FragmentType, graphql, useFragment } from "@repo/graphql-types";
import { iosOrAndroid, logError } from "@repo/lib";
import { useRouter, useSearch } from "@tanstack/react-router";
import { Share as CapacitorShare } from "@capacitor/share";
import { type MutableRefObject, useRef } from "react";
import { Directory, Filesystem } from "@capacitor/filesystem";
import { toBlob, toPng } from "html-to-image";
import { Button } from "@/components/button";
import { UnexpectedErrorDrawer } from "@/components/unexpected-error-drawer";
import {
  type PatientPaymentsRefundPayeeSectionFragment,
  PatientPaymentsRefundPayeeSection,
} from "@/components/patient-payments-refund-payee-section";
import {
  type PatientPaymentsRefundDetailsSectionFragment,
  PatientPaymentsRefundDetailsSection,
} from "@/components/patient-payments-refund-details-section";

export const RefundReceiptDetailsQueryFragment = graphql(`
  fragment RefundReceiptDetailsQueryFragment on ExpensumGetRefundReceiptOutput {
    ...PatientPaymentsRefundDetailsSectionFragment
    ...PatientPaymentsRefundPayeeSectionFragment
  }
`);

interface RefundReceiptDetailsProps {
  data: FragmentType<typeof RefundReceiptDetailsQueryFragment>;
}

export const RefundReceiptDetails = ({
  data,
}: RefundReceiptDetailsProps): JSX.Element => {
  const { guid, appointmentId, action } = useSearch({
    from: "/settings/payments/patient-payments/refund-receipt",
  });

  const refundData = useFragment(RefundReceiptDetailsQueryFragment, data);

  const router = useRouter();
  const receiptRef = useRef<HTMLDivElement | null>(null);

  const handleErrorDrawerDismiss = (value: boolean): void => {
    if (!value && action === "error") {
      void router.navigate({
        search: { guid, appointmentId, action: undefined },
        replace: true,
      });
    }
  };

  const onError = (): void => {
    void router.navigate({
      search: { guid, appointmentId, action: "error" },
      replace: true,
    });
  };

  const shareViaCapacitor = async (uri: string): Promise<void> => {
    await CapacitorShare.share({
      title: "Comprovante de Estorno",
      text: "Confira o comprovante do estorno realizado.",
      url: uri,
      dialogTitle: "Compartilhar Comprovante",
    });
  };

  const shareViaWeb = async (
    ref: MutableRefObject<HTMLDivElement | null>,
    dataUrl: string,
  ): Promise<void> => {
    if (!ref.current) {
      throw new Error("Receipt element not found!");
    }
    const blob = await toBlob(ref.current);

    if (!blob) {
      throw new Error("Failed to create blob from receipt!");
    }

    const file = new File([blob], "comprovante.png", { type: "image/png" });

    if (typeof navigator.share === "function") {
      await navigator.share({
        title: "Compartilhar Comprovante",
        text: "Confira este Comprovante!",
        files: [file],
      });
    } else {
      const link = document.createElement("a");
      link.href = dataUrl;
      link.download = "comprovante-estorno.png";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleShare = async (
    ref: MutableRefObject<HTMLDivElement | null>,
  ): Promise<void> => {
    if (!ref.current) {
      return;
    }
    try {
      const dataUrl = await toPng(ref.current);
      const fileName = `comprovante-estorno-${Date.now()}.png`;
      const { uri } = await Filesystem.writeFile({
        path: fileName,
        data: dataUrl.replace(/^data:image\/png;base64,/, ""),
        directory: Directory.Cache,
      });

      const canShare = await CapacitorShare.canShare();

      if (canShare.value && iosOrAndroid) {
        await shareViaCapacitor(uri);
      } else {
        await shareViaWeb(ref, dataUrl);
      }
    } catch (error) {
      logError(error);
      onError();
    }
  };
  return (
    <div className="flex flex-col gap-6">
      <div
        id="share-receipt"
        className="flex flex-col bg-white gap-2 p-2"
        ref={receiptRef}
      >
        <h2 className="text-lg font-semibold mb-1">Comprovante de estorno</h2>
        <p className="text-neutral-700 text-md mb-4">
          O estorno da sua cobrança foi efetuado com sucesso
        </p>
        <div className="flex flex-col gap-8">
          <PatientPaymentsRefundDetailsSection
            data={
              refundData as FragmentType<
                typeof PatientPaymentsRefundDetailsSectionFragment
              >
            }
          />
          <PatientPaymentsRefundPayeeSection
            data={
              refundData as FragmentType<typeof PatientPaymentsRefundPayeeSectionFragment>
            }
          />
        </div>
        <div className="bg-neutral-50 rounded-lg p-3 mt-4 text-md text-neutral-700">
          <span className="font-semibold">Atenção:</span> O crédito concedido dependerá da
          data de fechamento da fatura e só poderá ser realizado mediante ao banco emissor
          do cartão. Em caso de dúvida entre em contato com seu banco.
        </div>
      </div>
      <Button
        id="share-btn"
        onClick={() => {
          void handleShare(receiptRef);
        }}
        className="mt-4 w-full"
        variant="default"
      >
        Compartilhar comprovante
      </Button>
      <UnexpectedErrorDrawer
        open={action === "error"}
        setOpen={handleErrorDrawerDismiss}
      />
    </div>
  );
};
